import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FileText, Shield, RotateCcw, Building2, ShieldCheck, Loader2, type LucideIcon,
} from 'lucide-react';
import { Seo } from '@/seo/Seo';
import { breadcrumbSchema } from '@/seo/schemas';
import { configApi } from '@/api/configApi';

interface LegalDoc {
  id: string;
  label: string;
  order: number;
  content: string;
}

// Icons are presentation-only, mapped by document id (content lives in mongo).
const ICONS: Record<string, LucideIcon> = {
  terms: FileText,
  privacy: Shield,
  refund: RotateCcw,
  'clinic-terms': Building2,
  'clinic-privacy': ShieldCheck,
};

export default function Legal() {
  const { hash } = useLocation();
  const [docs, setDocs] = useState<LegalDoc[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    configApi
      .getLegalDocs()
      .then((res) => {
        if (!active) return;
        const list = (res.data?.documents ?? []).slice().sort((a, b) => a.order - b.order);
        setDocs(list);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  // Scroll to the section referenced by the URL hash once the docs are rendered.
  useEffect(() => {
    if (!hash || docs.length === 0) return;
    const el = document.getElementById(hash.replace('#', ''));
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [hash, docs]);

  return (
    <div className="mx-auto max-w-[1100px] px-5 py-10 sm:px-8 lg:px-12">
      <Seo
        title="Legal & Policies | Recovry"
        description="Recovry terms of service, privacy policy, refund and cancellation terms, and clinic partner policies."
        canonical="/legal"
        schema={[
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Legal & Policies', path: '/legal' },
          ]),
        ]}
      />

      <header className="mb-8">
        <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Legal &amp; Policies
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          The terms, privacy, and payment policies that govern your use of Recovry. Please read them carefully.
        </p>
      </header>

      {loading ? (
        <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
          <Loader2 className="mr-2 size-5 animate-spin" /> Loading…
        </div>
      ) : docs.length === 0 ? (
        <p className="rounded-xl border border-dashed p-10 text-center text-sm text-muted-foreground">
          Our policies are being updated. Please check back shortly.
        </p>
      ) : (
        <>
          {/* Mobile section jump chips */}
          <div className="mb-6 flex gap-2 overflow-x-auto pb-1 lg:hidden">
            {docs.map((s) => {
              const Icon = ICONS[s.id] ?? FileText;
              return (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  className="flex shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground"
                >
                  <Icon className="size-3.5" />
                  {s.label}
                </a>
              );
            })}
          </div>

          <div className="grid items-start gap-8 lg:grid-cols-[230px_1fr]">
            {/* Sticky section nav (desktop) */}
            <aside className="hidden lg:sticky lg:top-24 lg:block">
              <nav className="rounded-2xl border border-border bg-card p-3">
                <div className="mb-2 px-2 text-[10px] font-bold uppercase tracking-wider text-[var(--sc-hint)]">
                  On this page
                </div>
                {docs.map((s) => {
                  const Icon = ICONS[s.id] ?? FileText;
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-primary"
                    >
                      <Icon className="size-4 shrink-0" />
                      {s.label}
                    </a>
                  );
                })}
              </nav>
            </aside>

            {/* Documents */}
            <main className="min-w-0 space-y-6">
              {docs.map((s) => {
                const Icon = ICONS[s.id] ?? FileText;
                return (
                  <section key={s.id} id={s.id} className="scroll-mt-24">
                    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8">
                      <div className="mb-5 flex items-center gap-3 border-b border-border pb-4">
                        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-secondary text-primary">
                          <Icon className="size-4.5" />
                        </span>
                        <h2 className="text-xl font-bold tracking-tight text-foreground">{s.label}</h2>
                      </div>
                      <div className="legal-doc max-w-[68ch] [&_pre]:overflow-x-auto max-sm:[&_table]:block max-sm:[&_table]:overflow-x-auto">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{s.content}</ReactMarkdown>
                      </div>
                    </div>
                  </section>
                );
              })}
            </main>
          </div>
        </>
      )}
    </div>
  );
}
